import Phaser from 'phaser';
import type { ToolId } from '../../content/tools';
import { gameAudio } from '../audio/audio';
import { HUD_HEIGHT, TOOLBAR_HEIGHT } from '../layout';
import { ITEMS_KEY, itemFrame } from '../render/itemArt';
import { DEFAULT_GESTURES } from '../input/gestures';
import { button, HUD_TAP_MOVE, label, MessagePanel, onTap, UI } from '../ui/text';
import { drawCoin } from '../ui/widgets';
import { drawBigStar } from '../ui/chrome';
import { BOOST_BY_ID } from '../../content/boosts';
import type { BoostId } from '../../sim/boosts';

export interface HudInfo {
  label: string;
  percent: number;
  hp: number;
  maxHp: number;
  /** Coins in the wallet. */
  wallet: number;
  /**
   * Coins this attempt will add if the lawn is finished (picked up so far), or null where pickups
   * don't pay (daily, endless, test lawns).
   */
  pending: number | null;
  /** Tank level in [0, 1], or null for human-powered mowers. */
  fuel: number | null;
  /** Slow gear engaged, or null for mowers without a throttle. */
  slow: boolean | null;
  /** Carried tools, in slot order. */
  tools: { charges: number; ready: boolean }[];
  /** Boosts in stock (after the tools): how many, and how much of a running one is left (0..1). */
  boosts?: { count: number; usable: boolean; left: number }[];
  autopilot: boolean;
  /** Tints the label red (the storm boss: the rain is close). */
  danger?: boolean;
  /** A flat tire (thorn patch): shown by the hearts until a repair kit patches it. */
  flat?: boolean;
  /**
   * The label is the lawn's clock: gold while the par star can still be had (`ahead`), gray once
   * it can't (`behind`), plain on a lawn without one.
   */
  timer?: 'ahead' | 'behind' | 'plain';
}

/**
 * The card before a lawn (owner, 2026-10-23): what the lawn asks, its star goals lit where
 * already earned, and only the small print that helps.
 */
export interface StartCard {
  title: string;
  /** A boss lawn's goal, in a line. */
  goal?: string;
  /** The three star goals (campaign lawns), each lit if it was already earned. */
  goals: { text: string; earned: boolean }[];
  /** Small print: the mower (when it is new), tall grass. */
  notes: string[];
  /** How to start (and steer, early on). */
  hint: string;
}

const AUTOPILOT_TINT = 0x8fd3ff;
const LOW_FUEL = 0.25;
/** Text rows in the HUD band. */
const ROW1 = 3;
const ROW2 = 15;
const GAS = 0xffb03a;
/** Room kept for the clock (6 characters, 1:05.3), so the coins beside it never shift. */
const TIMER_W = 36;
/** The pause chip in the top-right corner (owner, 2026-10-23: MENU hid that it pauses). */
const PAUSE_W = 13;

/**
 * Top band overlay, two compact rows on the navy UI material (docs/ART_DIRECTION.md).
 * Row 1, what you are doing: the clock (or the lawn), coins, SLOW toggle, the pause button.
 * Row 2, what is changing: done % and bar, gas bar, toughness hearts.
 * Runs above the LevelScene.
 */
export class HudScene extends Phaser.Scene {
  private info: HudInfo | null = null;
  private text!: Phaser.GameObjects.BitmapText;
  private coinText!: Phaser.GameObjects.BitmapText;
  private pendingText!: Phaser.GameObjects.BitmapText;
  private percentText!: Phaser.GameObjects.BitmapText;
  private gasText!: Phaser.GameObjects.BitmapText;
  private gfx!: Phaser.GameObjects.Graphics;
  /** The pause button's tap area (it pauses, and the pause card has RETRY and MENU). */
  private menu!: Phaser.GameObjects.Zone;
  private message!: MessagePanel;
  private countdown: Phaser.GameObjects.Container | null = null;
  private throttle!: Phaser.GameObjects.BitmapText;
  private onThrottle: () => void = () => {};
  private onTool: (slot: number) => void = () => {};
  private toolLabels: string[] = [];
  private toolIcons: ToolId[] = [];
  /** One chip per carried tool: its icon, tapped to use it; charges are drawn in redraw(). */
  private toolChips: { icon: Phaser.GameObjects.Image; zone: Phaser.GameObjects.Zone }[] = [];
  private onBoost: (i: number) => void = () => {};
  private boostIcons: BoostId[] = [];
  private boostChips: {
    icon: Phaser.GameObjects.Image;
    zone: Phaser.GameObjects.Zone;
    count: Phaser.GameObjects.BitmapText;
  }[] = [];
  private toolBar: Phaser.GameObjects.Rectangle | null = null;
  private toolRule: Phaser.GameObjects.Rectangle | null = null;
  /** The tool and boost bar is hidden under a card too tall to fit above it (a tablet's shape). */
  private toolbarHidden = false;
  /** Message requested before create() ran (scene launch is asynchronous). */
  private pendingMessage: string | null = null;
  /** The start card, kept like the message (and redrawn on a resize). */
  private startCard: StartCard | null = null;
  private startBox: Phaser.GameObjects.Container | null = null;
  private artPicture: readonly string[] | null = null;
  private artCard: Phaser.GameObjects.Container | null = null;
  private onMenu: () => void = () => {};
  /** False until create() has built this run's objects (the scene instance is reused). */
  private caption: Phaser.GameObjects.Container | null = null;
  private notice: Phaser.GameObjects.Container | null = null;
  private ready = false;

  constructor() {
    super('Hud');
  }

  init(data: {
    onMenu: () => void;
    onThrottle?: () => void;
    onTool?: (slot: number) => void;
    toolLabels?: string[];
    toolIcons?: ToolId[];
    onBoost?: (i: number) => void;
    boostIcons?: BoostId[];
    /** Lawn Art: the picture to mow ('|' light, '-' dark, '.' none), shown on a small card. */
    artPicture?: readonly string[];
  }): void {
    this.artPicture = data.artPicture ?? null;
    this.onMenu = data.onMenu;
    this.onThrottle = data.onThrottle ?? (() => {});
    this.onTool = data.onTool ?? (() => {});
    this.toolLabels = data.toolLabels ?? [];
    this.toolIcons = data.toolIcons ?? [];
    this.onBoost = data.onBoost ?? (() => {});
    this.boostIcons = data.boostIcons ?? [];
    this.info = null;
    this.toolbarHidden = false;
    // pendingMessage is kept: the level may send its first prompt before this scene starts.
    this.ready = false;
  }

  create(): void {
    this.add.rectangle(0, 0, 4096, HUD_HEIGHT - 2, UI.panel).setOrigin(0, 0);
    this.add.rectangle(0, HUD_HEIGHT - 2, 4096, 1, UI.rule).setOrigin(0, 0);
    this.add.rectangle(0, HUD_HEIGHT - 1, 4096, 1, UI.shadow).setOrigin(0, 0);
    this.gfx = this.add.graphics();
    this.text = label(this, 4, ROW1, '');
    this.coinText = label(this, 0, ROW1, '', 1, UI.accent);
    this.pendingText = label(this, 0, ROW1, '', 1, UI.good);
    this.percentText = label(this, 4, ROW2, '', 1, UI.accent);
    this.gasText = label(this, 0, ROW2, 'GAS', 1, UI.dim).setVisible(false);
    // The whole top-right corner of the bar pauses (owner, 2026-10-29: the chip alone was a small
    // target). Taps on the bar never steer, and the throttle button sits above this zone.
    this.menu = this.add
      .zone(0, 0, PAUSE_W + 16, HUD_HEIGHT)
      .setOrigin(1, 0)
      .setData('label', 'PAUSE')
      .setInteractive({ useHandCursor: true });
    onTap(
      this.menu,
      () => {
        gameAudio.play('click');
        this.onMenu();
      },
      HUD_TAP_MOVE,
    );
    this.message = new MessagePanel(this, 176).setVisible(false);
    this.throttle = button(this, 0, ROW1, 'SLOW', () => this.onThrottle(), 1, UI.text, true);
    this.throttle.setVisible(false);
    if (this.toolLabels.length + this.boostIcons.length > 0) {
      this.toolBar = this.add.rectangle(0, 0, 4096, TOOLBAR_HEIGHT, UI.panel).setOrigin(0, 0);
      this.toolRule = this.add.rectangle(0, 0, 4096, 1, UI.rule).setOrigin(0, 0);
    } else {
      this.toolBar = null;
      this.toolRule = null;
    }
    // The toolbar backgrounds go to the back so the HUD graphics (gauges, chip backgrounds) draw
    // over them; texts and tool icons stay on top of the graphics.
    if (this.toolRule) this.children.sendToBack(this.toolRule);
    if (this.toolBar) this.children.sendToBack(this.toolBar);
    // Chips fire on a tap only, read at the scene level (onBarTap): a chip that took touches
    // itself would capture them from the lawn below, and a swipe starting on the bar must still
    // steer. The zones hit nothing; they keep the chip's label and 'pointerup' for the e2e harness.
    const chipZone = (name: string, fire: () => void): Phaser.GameObjects.Zone =>
      this.add
        .zone(0, 0, 10, 10)
        .setOrigin(0, 0)
        .setData('label', name)
        .setData('fire', fire)
        .setInteractive({ hitArea: new Phaser.Geom.Rectangle(), hitAreaCallback: () => false })
        .on('pointerup', fire);
    this.toolChips = this.toolIcons.map((id, slot) => ({
      icon: this.add.image(0, 0, ITEMS_KEY, itemFrame(id)).setOrigin(0, 0),
      zone: chipZone(this.toolLabels[slot] ?? id, () => this.onTool(slot)),
    }));
    this.boostChips = this.boostIcons.map((id, i) => ({
      icon: this.add.image(0, 0, ITEMS_KEY, itemFrame(id)).setOrigin(0, 0),
      zone: chipZone(BOOST_BY_ID[id].short, () => this.onBoost(i)),
      count: label(this, 0, 0, '', 1, UI.accent),
    }));
    this.ready = true;
    if (this.startCard) this.drawStartCard(this.startCard);
    else if (this.pendingMessage !== null) this.showMessage(this.pendingMessage);
    const info = this.info;
    this.info = null;
    if (info) this.setInfo(info);
    if (this.artPicture) this.artCard = this.drawArtCard(this.artPicture);
    this.layout();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.layout);
    this.input.on('pointerup', this.onBarTap);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off(Phaser.Scale.Events.RESIZE, this.layout);
      this.input.off('pointerup', this.onBarTap);
      // Objects are destroyed now; buffer anything sent before the next create().
      this.ready = false;
      this.startBox = null;
    });
  }

  setInfo(info: HudInfo): void {
    const prev = this.info;
    // (Its tool and boost lists are new every frame: compared by what they hold, so an unchanged
    // HUD is not redrawn each frame.)
    if (prev && sameInfo(prev, info)) return;
    this.info = info;
    if (!this.ready) return;
    this.text
      .setText(info.label)
      .setTint(
        info.danger
          ? UI.danger
          : info.autopilot
            ? AUTOPILOT_TINT
            : info.timer === 'ahead'
              ? UI.accent
              : info.timer === 'behind'
                ? UI.dim
                : UI.text,
      );
    this.coinText.setText(String(info.wallet));
    this.pendingText.setText(info.pending ? `+${info.pending}` : '').setVisible(!!info.pending);
    // A coin picked up: the pending count pops.
    if (prev && (info.pending ?? 0) > (prev.pending ?? 0)) {
      this.tweens.killTweensOf(this.pendingText);
      this.tweens.add({
        targets: this.pendingText,
        scale: { from: 1.6, to: 1 },
        duration: 220,
        ease: 'Back.easeOut',
      });
    }
    this.percentText.setText(`${info.percent}%`);
    // The tank dropping to a quarter: a word mid-screen (again after a refill runs low).
    if (prev?.fuel != null && info.fuel !== null && prev.fuel > LOW_FUEL && info.fuel <= LOW_FUEL) {
      this.showNotice('LOW GAS', UI.danger);
    }
    this.redraw();
  }

  /**
   * A short heads-up over the lawn (owner, 2026-10-07: "low gas" and the like): a small chip a
   * third of the way down, fading in and out on its own. Never takes a tap.
   */
  showNotice(text: string, color: number = UI.accent): void {
    if (!this.ready) return;
    this.notice?.destroy();
    const { width, height } = this.scale.gameSize;
    const t = label(this, 0, 0, text, 1, color);
    const w = Math.ceil(t.width) + 14;
    const h = 15;
    const box = this.add.graphics();
    box.fillStyle(UI.shadow, 0.8).fillRect(-w / 2 + 1, -h / 2 + 1, w, h);
    box.fillStyle(UI.panel, 0.92).fillRect(-w / 2, -h / 2, w, h);
    box.lineStyle(1, color).strokeRect(-w / 2 + 0.5, -h / 2 + 0.5, w - 1, h - 1);
    t.setPosition(-Math.floor(t.width / 2), -Math.floor(t.height / 2) + 1);
    const cy = Math.round(HUD_HEIGHT + (height - HUD_HEIGHT) * 0.3);
    const notice = this.add.container(Math.round(width / 2), cy, [box, t]).setAlpha(0);
    this.notice = notice;
    this.tweens.chain({
      targets: notice,
      tweens: [
        { alpha: 1, duration: 180 },
        { alpha: 1, duration: 1500 },
        { alpha: 0, duration: 500 },
      ],
      onComplete: () => {
        notice.destroy();
        if (this.notice === notice) this.notice = null;
      },
    });
  }

  /**
   * Centered message over the lawn area. Lives here, on the unzoomed HUD camera, so it stays
   * crisp and positioned regardless of the lawn camera's zoom.
   */
  showMessage(text: string): void {
    // Kept until hidden: when the level restarts (NEXT, RETRY), this scene is stopped and started
    // again just after the new level sends its first prompt, and shows it again in create().
    this.pendingMessage = text;
    this.startCard = null;
    this.startBox?.destroy();
    this.startBox = null;
    if (!this.ready) return;
    const { width, height } = this.scale.gameSize;
    const lawnCenterY = HUD_HEIGHT + (height - HUD_HEIGHT) / 2;
    this.message.setMessage(text, width / 2, lawnCenterY, width - 16);
  }

  /** A big countdown chip ('3', '2', '1', 'GO!') under the HUD; pops in on each change. */
  showCountdown(text: string): void {
    if (!this.ready) return;
    const { width } = this.scale.gameSize;
    this.countdown?.destroy();
    const t = label(this, 0, 0, text, 2, text === 'GO!' ? UI.good : UI.accent);
    const w = Math.ceil(t.width) + 20;
    const h = 30;
    const box = this.add.graphics();
    box.fillStyle(UI.shadow).fillRect(-w / 2 + 2, -h / 2 + 2, w, h);
    box.fillStyle(UI.panel).fillRect(-w / 2, -h / 2, w, h);
    box.lineStyle(1, UI.rule).strokeRect(-w / 2 + 0.5, -h / 2 + 0.5, w - 1, h - 1);
    t.setPosition(-Math.floor(t.width / 2), -Math.floor(t.height / 2));
    // Just under the HUD, so it never hides the lawn the player is reading.
    const cy = HUD_HEIGHT + 22;
    this.countdown = this.add.container(Math.round(width / 2), cy, [box, t]);
    this.tweens.add({
      targets: this.countdown,
      scale: { from: 1.5, to: 1 },
      duration: 180,
      ease: 'Back.easeOut',
    });
  }

  hideCountdown(): void {
    this.countdown?.destroy();
    this.countdown = null;
  }

  /** The coin icon's center (screen pixels): where reward coins fly to. */
  walletTarget(): { x: number; y: number } {
    return { x: Math.round(this.text.x + this.labelWidth() + 13), y: ROW1 + 3 };
  }

  /** A coin landed: the wallet pops. */
  popWallet(): void {
    if (!this.ready) return;
    this.tweens.killTweensOf(this.coinText);
    this.coinText.setScale(1);
    this.tweens.add({
      targets: this.coinText,
      scale: { from: 1.5, to: 1 },
      duration: 180,
      ease: 'Back.easeOut',
    });
  }

  /**
   * A small caption on a band at the bottom of the screen (null hides it). An `alert` (the
   * molehill hint; owner, 2026-10-29: the navy band was easy to miss) is black on the yellow of the
   * blinking molehill outline.
   */
  showCaption(text: string | null, alert = false): void {
    this.caption?.destroy();
    this.caption = null;
    if (!this.ready || text === null) return;
    const { width, height } = this.scale.gameSize;
    const t = label(this, 0, 0, text, 1, alert ? 0x000000 : UI.text).setOrigin(0.5, 0);
    if (alert) t.setDropShadow(0, 0, 0, 0);
    const bg = alert
      ? this.add.rectangle(0, -3, width, 13, UI.accent).setOrigin(0.5, 0)
      : this.add.rectangle(0, -3, width, 13, UI.panel, 0.9).setOrigin(0.5, 0);
    this.caption = this.add.container(Math.round(width / 2), height - 16, [bg, t]);
  }

  /** The tool and boost bar's height at the bottom (0 without one): cards keep above it. */
  get toolbarHeight(): number {
    return this.toolBar ? TOOLBAR_HEIGHT : 0;
  }

  /** Hides the tool and boost bar (under a card that needs the room), or shows it again. */
  setToolbarHidden(hidden: boolean): void {
    this.toolbarHidden = hidden;
    this.toolBar?.setVisible(!hidden);
    this.toolRule?.setVisible(!hidden);
    for (const c of this.toolChips) c.icon.setVisible(!hidden);
    for (const c of this.boostChips) {
      c.icon.setVisible(!hidden);
      c.count.setVisible(!hidden);
    }
  }

  hideMessage(): void {
    this.pendingMessage = null;
    this.startCard = null;
    this.startBox?.destroy();
    this.startBox = null;
    if (this.ready) this.message.setVisible(false);
  }

  /** The card before a lawn, over the lawn's middle; kept until hidden, like a message. */
  showStartCard(card: StartCard): void {
    this.pendingMessage = null;
    this.startCard = card;
    if (!this.ready) return;
    this.message.setVisible(false);
    this.drawStartCard(card);
  }

  private drawStartCard(card: StartCard): void {
    this.startBox?.destroy();
    const { width, height } = this.scale.gameSize;
    const w = Math.min(width - 16, 176);
    const inner = w - 12;
    const bg = this.add.graphics();
    const g = this.add.graphics();
    const parts: Phaser.GameObjects.GameObject[] = [bg, g];
    let y = 6;
    const centered = (text: string, color: number) => {
      const t = label(this, 0, y, text, 1, color).setMaxWidth(inner).setCenterAlign();
      t.setX(Math.round((w - t.width) / 2));
      parts.push(t);
      y += Math.ceil(t.height) + 5;
    };
    centered(card.title, UI.accent);
    if (card.goal) centered(card.goal, UI.text);
    if (card.goals.length > 0) {
      y += 1;
      for (const goal of card.goals) {
        drawBigStar(g, 8, y, goal.earned);
        const t = label(this, 19, y, goal.text, 1, UI.text).setMaxWidth(inner - 13);
        parts.push(t);
        y += Math.max(8, Math.ceil(t.height)) + 4;
      }
      y += 2;
    }
    for (const note of card.notes) centered(note, UI.dim);
    centered(card.hint, UI.text);
    const h = y + 1;
    bg.fillStyle(UI.panel, UI.panelAlpha).fillRect(0, 0, w, h);
    bg.lineStyle(1, UI.dim, 0.6).strokeRect(0.5, 0.5, w - 1, h - 1);
    const cy = HUD_HEIGHT + (height - HUD_HEIGHT) / 2;
    this.startBox = this.add.container(Math.round((width - w) / 2), Math.round(cy - h / 2), parts);
  }

  /**
   * Lawn Art's reference card: the picture to mow in its two tones, small, on a navy card in the
   * bottom-right corner (2 px a tile, cropped to the picture).
   */
  private drawArtCard(rows: readonly string[]): Phaser.GameObjects.Container {
    const ys = rows.flatMap((r, y) => (/[|-]/.test(r) ? [y] : []));
    const xs = rows.flatMap((r) => [...r].flatMap((c, x) => (c === '.' ? [] : [x])));
    const [x0, x1] = [Math.min(...xs), Math.max(...xs)];
    const [y0, y1] = [ys[0] ?? 0, ys[ys.length - 1] ?? 0];
    const k = 2;
    const w = (x1 - x0 + 1) * k + 6;
    const h = (y1 - y0 + 1) * k + 6;
    const g = this.add.graphics();
    g.fillStyle(UI.shadow, 0.7).fillRect(1, 1, w, h);
    g.fillStyle(UI.panel, 0.92).fillRect(0, 0, w, h);
    g.lineStyle(1, UI.panelHi).strokeRect(0.5, 0.5, w - 1, h - 1);
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const c = rows[y]?.[x];
        if (c !== '|' && c !== '-') continue;
        g.fillStyle(c === '|' ? 0x6fcd63 : 0x1f6a2a).fillRect(
          3 + (x - x0) * k,
          3 + (y - y0) * k,
          k,
          k,
        );
      }
    }
    return this.add.container(0, 0, [g]).setSize(w, h);
  }

  /**
   * A toolbar chip fires for a tap that begins on it, or in the gap beside it, moving less than a
   * swipe would (a swipe from the bar steers instead; one that merely ends there uses nothing).
   * (Owner, 2026-11-04: taps went missing; the old rule wanted a still finger inside the chip.)
   */
  private readonly onBarTap = (p: Phaser.Input.Pointer): void => {
    if (this.toolbarHidden || p.wasCanceled) return;
    const moved = Math.max(Math.abs(p.upX - p.downX), Math.abs(p.upY - p.downY));
    if (moved >= DEFAULT_GESTURES.swipeThreshold) return;
    const { height } = this.scale.gameSize;
    if (p.downY < height - TOOLBAR_HEIGHT) return;
    const zones = [...this.toolChips, ...this.boostChips].map((c) => c.zone);
    const zone = zones.find((z) => {
      const b = z.getBounds();
      return p.downX >= b.x - CHIP_SLOP && p.downX < b.right + CHIP_SLOP;
    });
    if (zone) (zone.getData('fire') as () => void)();
  };

  private readonly layout = (): void => {
    const { width, height } = this.scale.gameSize;
    if (this.artCard) {
      const bottom = height - (this.toolBar ? TOOLBAR_HEIGHT : 0) - 4;
      this.artCard.setPosition(width - this.artCard.width - 4, bottom - this.artCard.height);
    }
    this.menu.setPosition(width, 0);
    this.toolBar?.setPosition(0, height - TOOLBAR_HEIGHT);
    this.toolRule?.setPosition(0, height - TOOLBAR_HEIGHT);
    const n = this.toolChips.length + this.boostChips.length;
    this.toolChips.forEach(({ icon, zone }, i) => {
      const { x, w } = toolChipRect(i, n, width);
      icon.setPosition(x + 4, height - TOOLBAR_HEIGHT + 4);
      zone.setPosition(x, height - TOOLBAR_HEIGHT + 2).setSize(w, TOOLBAR_HEIGHT - 3);
    });
    this.boostChips.forEach(({ icon, zone, count }, i) => {
      const { x, w } = toolChipRect(this.toolChips.length + i, n, width);
      icon.setPosition(x + 3, height - TOOLBAR_HEIGHT + 4);
      zone.setPosition(x, height - TOOLBAR_HEIGHT + 2).setSize(w, TOOLBAR_HEIGHT - 3);
      count.setPosition(x + w - 7, height - TOOLBAR_HEIGHT + 8);
    });
    if (this.message.visible) {
      this.message.container.setPosition(width / 2, HUD_HEIGHT + (height - HUD_HEIGHT) / 2);
    }
    if (this.startBox && this.startCard) this.drawStartCard(this.startCard);
    this.redraw();
  };

  /** The label's width, or the clock's room while it shows the clock. */
  private labelWidth(): number {
    return this.info?.timer ? Math.max(this.text.width, TIMER_W) : this.text.width;
  }

  private redraw(): void {
    const info = this.info;
    const g = this.gfx;
    g.clear();
    if (!info) return;
    const { width } = this.scale.gameSize;

    // The pause chip: two bars.
    const pauseX = width - 2 - PAUSE_W;
    g.fillStyle(UI.shadow).fillRect(pauseX, ROW1 - 2, PAUSE_W, 11);
    g.fillStyle(UI.panelHi).fillRect(pauseX + 1, ROW1 - 1, PAUSE_W - 2, 9);
    g.fillStyle(UI.text)
      .fillRect(pauseX + 4, ROW1 + 1, 2, 5)
      .fillRect(pauseX + 7, ROW1 + 1, 2, 5);

    // Row 1: coin icon, wallet and the coins pending this attempt after the lawn label, then the
    // throttle toggle.
    const coinX = Math.round(this.text.x + this.labelWidth() + 10);
    drawHudCoin(g, coinX, ROW1);
    this.coinText.setPosition(coinX + 10, ROW1);
    this.pendingText.setPosition(Math.round(this.coinText.x + this.coinText.width + 4), ROW1);
    const coinsEnd = this.pendingText.visible
      ? this.pendingText.x + this.pendingText.width
      : this.coinText.x + this.coinText.width;
    this.throttle.setVisible(info.slow !== null);
    if (info.slow !== null) {
      this.throttle
        .setText(info.slow ? 'FAST' : 'SLOW')
        .setTint(info.slow ? AUTOPILOT_TINT : UI.text)
        .setPosition(Math.round(coinsEnd + 10), ROW1);
      // A long label and a big wallet: the pending count gives way before the gear runs into MENU.
      if (this.throttle.x + this.throttle.width > pauseX - 4 && this.pendingText.visible) {
        this.pendingText.setVisible(false);
        this.throttle.setX(Math.round(this.coinText.x + this.coinText.width + 10));
      }
    }

    // Row 2: done % and its bar.
    const barY = ROW2 + 1;
    const pctBarX = Math.round(this.percentText.x + this.percentText.width + 4);
    gauge(g, pctBarX, barY, 34, info.percent / 100, UI.good);

    // Toughness hearts, right-aligned.
    const heartW = 8;
    let x = width - 6 - info.maxHp * heartW + 1;
    for (let i = 0; i < info.maxHp; i++) drawHeart(g, x + i * heartW, ROW2, i < info.hp);
    if (info.flat) drawFlatTire(g, x - 10, ROW2);
    // Where the gas gauge has to stop (a sturdy mower's row of hearts is long).
    const gasEnd = x - (info.flat ? 14 : 4);

    // Gas gauge between them (human-powered mowers have none).
    this.gasText.setVisible(info.fuel !== null);
    if (info.fuel !== null) {
      const gx = pctBarX + 34 + 8;
      this.gasText.setPosition(gx, ROW2);
      const fuel = Math.max(0, Math.min(1, info.fuel));
      x = gx + this.gasText.width + 4;
      gauge(g, x, barY, Math.min(34, gasEnd - x), fuel, fuel <= LOW_FUEL ? UI.danger : GAS);
    }

    // Tool chips: icon + charge pips; dimmed while recharging or empty. Sized as layout() places
    // them: one slot each for the tools and the boosts.
    const { height } = this.scale.gameSize;
    const n = info.tools.length + (info.boosts?.length ?? 0);
    if (this.toolbarHidden) return;
    info.tools.forEach((t, i) => {
      const chip = this.toolChips[i];
      if (!chip) return;
      const { x, w } = toolChipRect(i, n, width);
      const top = height - TOOLBAR_HEIGHT + 2;
      g.fillStyle(UI.shadow).fillRect(x - 1, top - 1, w + 2, TOOLBAR_HEIGHT - 1);
      g.fillStyle(t.ready ? UI.panelHi : 0x2a2550).fillRect(x, top, w, TOOLBAR_HEIGHT - 3);
      chip.icon.setAlpha(t.ready ? 1 : 0.45);
      for (let k = 0; k < t.charges; k++) {
        g.fillStyle(t.ready ? UI.accent : UI.dim).fillRect(x + 24 + k * 6, top + 8, 4, 4);
      }
    });

    // Boost chips: icon and stock; a running boost is framed, with what is left draining away.
    info.boosts?.forEach((b, i) => {
      const chip = this.boostChips[i];
      if (!chip) return;
      const { x, w } = toolChipRect(info.tools.length + i, n, width);
      const top = height - TOOLBAR_HEIGHT + 2;
      const running = b.left > 0;
      g.fillStyle(UI.shadow).fillRect(x - 1, top - 1, w + 2, TOOLBAR_HEIGHT - 1);
      g.fillStyle(b.usable || running ? UI.panelHi : 0x2a2550).fillRect(
        x,
        top,
        w,
        TOOLBAR_HEIGHT - 3,
      );
      if (running) {
        g.lineStyle(1, UI.accent).strokeRect(x + 0.5, top + 0.5, w - 1, TOOLBAR_HEIGHT - 4);
        g.fillStyle(UI.accent).fillRect(
          x + 1,
          top + TOOLBAR_HEIGHT - 6,
          Math.round((w - 2) * b.left),
          2,
        );
      }
      chip.icon.setAlpha(b.usable || running ? 1 : 0.45);
      chip.count.setText(String(b.count)).setTint(b.usable ? UI.accent : UI.dim);
    });
  }
}

/** A 1 px-framed bar: ink well, `color` fill for `frac` of its width. */
function gauge(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  frac: number,
  color: number,
): void {
  g.fillStyle(UI.dim).fillRect(x - 1, y - 1, w + 2, 7);
  g.fillStyle(UI.shadow).fillRect(x, y, w, 5);
  g.fillStyle(color).fillRect(x, y, Math.round(w * Math.max(0, Math.min(1, frac))), 5);
}

const HEART = ['.##.##.', '#######', '#######', '.#####.', '..###..', '...#...'];

function drawHeart(g: Phaser.GameObjects.Graphics, x: number, y: number, full: boolean): void {
  g.fillStyle(full ? UI.danger : 0x3a3366);
  HEART.forEach((row, dy) => {
    for (let dx = 0; dx < row.length; dx++) if (row[dx] === '#') g.fillRect(x + dx, y + dy, 1, 1);
  });
  if (full) g.fillStyle(0xffffff).fillRect(x + 1, y + 1, 1, 1);
}

const FLAT_TIRE = ['.###.', '#...#', '#.#.#', '#...#', '##.##', '.###.', '#####'];

/** A squashed tire, by the hearts: the mower has a flat. */
function drawFlatTire(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
  FLAT_TIRE.forEach((row, dy) => {
    for (let dx = 0; dx < row.length; dx++) {
      if (row[dx] !== '#') continue;
      g.fillStyle(dy === 6 ? UI.danger : 0xd9d4e8).fillRect(x + dx, y + dy, 1, 1);
    }
  });
}

function drawHudCoin(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
  drawCoin(g, x, y, UI.shadow);
}

/** Where tool chip `i` of `n` sits in the toolbar. */
/** Whether two HUD states show the same: every field, the lists entry by entry. */
function sameInfo(a: HudInfo, b: HudInfo): boolean {
  return (Object.keys(b) as (keyof HudInfo)[]).every((k) => {
    const x = a[k];
    const y = b[k];
    if (!Array.isArray(x) || !Array.isArray(y)) return x === y;
    return (
      x.length === y.length &&
      x.every((e: object, i) => {
        const f = y[i] as object;
        return (Object.keys(f) as (keyof typeof f)[]).every((key) => e[key] === f[key]);
      })
    );
  });
}

/** A tap this far beside a chip (half the gap between two) still counts as on it. */
const CHIP_SLOP = 2;

function toolChipRect(i: number, n: number, width: number): { x: number; w: number } {
  const gap = 4;
  const w = Math.min(64, Math.floor((width - 8 - gap * (n - 1)) / Math.max(1, n)));
  return { x: 4 + i * (w + gap), w };
}
